import React, { useEffect, useRef, useState, useMemo } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import TextInput from "../../components/Common/TextInput";
import Button from "../../components/Common/Button";
import { Link } from "react-router-dom";
import { useAccounts } from "../../DataStore/AccountsContext";
import { useTheme } from "../../DataStore/ThemeContext";
import Notification from "../../components/Common/Notification";
import ajaxPeriod from "../../util/remote/ajaxPeriod";
import StepNotes from "../../components/MigrationAssistant/StepNotes";

const steps = [
  { title: "Set Financial Year", icon: "fa fa-calendar-alt" },
  { title: "Previous Year Net Result", icon: "fa fa-chart-line" },
  { title: "Asset Balances", icon: "fa fa-building" },
  { title: "Liability Balances", icon: "fa fa-scale-balanced" },
  { title: "Summary", icon: "fa fa-clipboard-check" },
];

const fmtUGX = (n) => `UGX ${(Number(n) || 0).toLocaleString()}`;

const MigrationAssistantPage = () => {
  const { setNotify } = useTheme();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    opening_date: "",
    closing_date: "",
    net_revenue: "",
    assets: [{ accountId: "", accountName: "", amount: "" }],
    liabilities: [{ accountId: "", accountName: "", amount: "" }],
  });

  const firstFieldRef = useRef();
  const {
    getAssetAccounts,
    assetAccountListing,
    liabilityAccountListing,
    getLiabilityAccounts,
  } = useAccounts();

  useEffect(() => {
    getAssetAccounts();
    getLiabilityAccounts();
  }, []);

  useEffect(() => {
    if (firstFieldRef.current) firstFieldRef.current.focus?.();
  }, [step]);

  const handleChange = (field, value, index = null, type = null) => {
    if (["assets", "liabilities"].includes(field)) {
      const copy = [...form[field]];

      if (type === "select") {
        // ✅ handle clear
  if (!value) {
    copy[index] = { ...copy[index], accountId: "", accountName: "" };
    setForm({ ...form, [field]: copy });
    return;
  }

  const source = field === "assets" ? assetAccountListing : liabilityAccountListing;
  const selected = source?.find((a) => a.account_id === value);

        if (selected) {
          const exists = copy.some((entry, i) => i !== index && entry.accountId === value);
          if (exists) {
            setNotify(<Notification type="danger" message="Account already selected." />);
            return;
          }

          copy[index] = {
            ...copy[index],
            accountId: selected.account_id,
            accountName: `${selected.account_number} - ${selected.account_name}`,
          };
        }
      } else {
        copy[index][type] = value;
      }

      setForm({ ...form, [field]: copy });
    } else {
      setForm({ ...form, [field]: value });
    }
  };

  const addItem = (field) =>
    setForm({
      ...form,
      [field]: [...form[field], { accountId: "", accountName: "", amount: "" }],
    });

  const removeItem = (field, index) => {
    const copy = [...form[field]];
    copy.splice(index, 1);
    setForm({ ...form, [field]: copy });
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return form.opening_date && form.closing_date;
      case 2:
        return form.net_revenue !== "" && !isNaN(form.net_revenue);
      case 3:
        return form.assets.length > 0 && form.assets.every((a) => a.accountId && a.amount);
      case 4:
        return (
          form.liabilities.length > 0 &&
          form.liabilities.every((l) => l.accountId && l.amount)
        );
      default:
        return true;
    }
  };

  const filterOptions = (list, selected) =>
    (Array.isArray(list) ? list.filter((a) => !a.sub_accounts && !selected.includes(a.account_id)) : [])
      .map((a) => ({ label: `${a.account_number} - ${a.account_name}`, value: a.account_id }));

  const assetTotal = useMemo(
    () => form.assets.reduce((acc, a) => acc + parseFloat(a.amount || 0), 0),
    [form.assets]
  );
  const liabilityTotal = useMemo(
    () => form.liabilities.reduce((acc, l) => acc + parseFloat(l.amount || 0), 0),
    [form.liabilities]
  );
  const netRevenueNum = useMemo(() => parseFloat(form.net_revenue || 0), [form.net_revenue]);

  const balanced = useMemo(
    () => assetTotal === liabilityTotal + netRevenueNum,
    [assetTotal, liabilityTotal, netRevenueNum]
  );

  const progressPct = useMemo(() => Math.round((step / steps.length) * 100), [step]);

  const renderStepNavigation = () => (
    <div className="mb-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="text-muted" style={{ fontSize: 12 }}>
          Step {step} of {steps.length}
        </div>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {progressPct}%
        </div>
      </div>

      <div
        className="progress"
        style={{ height: 8, borderRadius: 999, background: "rgba(0,0,0,.06)" }}
      >
        <div
          className="progress-bar"
          role="progressbar"
          style={{ width: `${progressPct}%`, borderRadius: 999 }}
          aria-valuenow={progressPct}
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>

      <div className="d-flex flex-wrap mt-3" style={{ gap: 10 }}>
        {steps.map((s, idx) => {
          const current = idx + 1;
          const isActive = current === step;
          const isDone = current < step;

          return (
            <Link
              key={s.title}
              to="#"
              onClick={(e) => {
                e.preventDefault();
                // allow back navigation freely; forward only if current step valid
                if (current <= step) setStep(current);
              }}
              className="text-decoration-none"
              style={{ flex: "0 0 auto" }}
            >
              <span
                className="d-inline-flex align-items-center border"
                style={{
                  padding: "8px 12px",
                  borderRadius: 999,
                  background: isActive ? "rgba(0,123,255,.08)" : isDone ? "rgba(40,167,69,.10)" : "#fff",
                  borderColor: isActive ? "rgba(0,123,255,.25)" : "rgba(0,0,0,.08)",
                  color: isActive ? "#0d6efd" : isDone ? "#198754" : "rgba(0,0,0,.65)",
                  fontWeight: 600,
                  fontSize: 12,
                  boxShadow: "0 1px 2px rgba(16,24,40,.04)",
                }}
              >
                <i className={`${s.icon} mr-2`} />
                {s.title}
                {isDone && <i className="fa fa-check ml-2" />}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );

  const handleMigrate = async (data) => {
    if (loading) return;

    if (!data.balanced) {
      setNotify(
        <Notification
          type="danger"
          message="Assets and Liabilities do not balance with the previous net result. Please check your entries."
        />
      );
      return;
    }

    setLoading(true);
    const server_response = await ajaxPeriod.migrateBalances({ migration_data: data });
    setLoading(false);

    if (server_response.status === "OK") {
      setNotify(<Notification type="success" message={server_response.message} />);
      setLoading(true);
      setTimeout(() => (window.location.href = "/"), 5000);
    } else {
      setNotify(<Notification type="danger" message={server_response.message} />);
    }
  };

  const renderRepeater = (field, listing, selectedIds, refreshFn, label) => (
  <>
    {/* Header (only once) */}
    <div className="d-flex align-items-center justify-content-between mb-2">
      <div>
        <div className="font-weight-semibold" style={{ fontSize: 14 }}>Select account and enter its opening balance.</div>
        {/* <div className="text-muted" style={{ fontSize: 12 }}>
          Select account and enter its opening balance.
        </div> */}
      </div>

      <button
        type="button"
        className="btn btn-light border btn-sm"
        onClick={() => refreshFn(true)}
        style={{ borderRadius: 12, fontWeight: 600 }}
      >
        <i className="fa fa-rotate mr-2" />
        Refresh
      </button>
    </div>
    <div className="overflow-auto" style={{maxHeight: '300px'}}>
    {form[field].map((row, i) => (
      <div
        key={i}
        className="border bg-white mb-2 position-relative"
        style={{
          borderRadius: 16,
          padding: 14,
          boxShadow: "0 1px 2px rgba(16,24,40,.04)",
        }}
      >
        {/* Delete button pinned to the card */}
        <button
          type="button"
          onClick={() => removeItem(field, i)}
          className="btn btn-light border btn-sm"
          title="Remove row"
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 50,          // ✅ key: stay above select/input overlays
            borderRadius: 12,
            width: 38,
            height: 38,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "auto", // ✅ ensures it can receive clicks
          }}
        >
        <i className="fa fa-trash-alt text-danger" />
      </button>

        <div className="row align-items-end">
          {/* Account select */}
          <div className="col-md-6 mb-2 mb-md-0">
            <TextInput
              type="select"
              placeholder={row.accountName || "Select Account"}
              value={row.accountId}
              options={filterOptions(listing, selectedIds.filter((_, idx) => idx !== i))}
              onChange={(item) => handleChange(field, item?.value || "", i, "select")}
              iconClass="fa fa-university"
              leftComponent={
                <i className="fa fa-university link" onClick={() => refreshFn(true)} />
              }
              loading={!listing}
            />
          </div>

          {/* Amount */}
          <div className="col-md-6">
            <TextInput
              placeholder="Amount"
              type="number"
              value={row.amount}
              onChange={(e) => handleChange(field, e.target.value, i, "amount")}
              // leftIcon="fa fa-money-bill"
            />
          </div>
        </div>
      </div>
    ))}

    <Button
      label={`Add ${label.replace("Balances", "").trim()}`}
      className="btn btn-outline-primary"
      onClick={() => addItem(field)}
    />
    </div>
  </>
);


  const renderStepContent = () => {
    const assetIds = form.assets.map((a) => a.accountId);
    const liabilityIds = form.liabilities.map((l) => l.accountId);

    switch (step) {
      case 1:
        return (
          <>
            <TextInput
              label="Start Date"
              type="date"
              value={form.opening_date}
              onChange={(e) => handleChange("opening_date", e.target.value)}
              inputRef={firstFieldRef}
              leftIcon="fa fa-calendar-alt"
            />
            <TextInput
              label="End Date"
              type="date"
              value={form.closing_date}
              onChange={(e) => handleChange("closing_date", e.target.value)}
              leftIcon="fa fa-calendar-alt"
            />
          </>
        );

      case 2:
        return (
          <TextInput
            label="Net Profit / Loss (use negative sign for loss)"
            placeholder="e.g. -93780187"
            value={form.net_revenue}
            onChange={(e) => handleChange("net_revenue", e.target.value)}
            inputRef={firstFieldRef}
            leftIcon="fa fa-money-bill"
            type="number"
          />
        );

      case 3:
        return renderRepeater(
          "assets",
          assetAccountListing,
          assetIds,
          getAssetAccounts,
          "Asset Balances"
        );

      case 4:
        return renderRepeater(
          "liabilities",
          liabilityAccountListing,
          liabilityIds,
          getLiabilityAccounts,
          "Liability Balances"
        );

      case 5:
        return (
          <>
            <div className="row">
              <div className="col-md-6 mb-2">
                <div className="border bg-white p-3" style={{ borderRadius: 16 }}>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Financial Year
                  </div>
                  <div className="font-weight-semibold">
                    {form.opening_date} → {form.closing_date}
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-2">
                <div className="border bg-white p-3" style={{ borderRadius: 16 }}>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Previous Net Result
                  </div>
                  <div className="font-weight-semibold">{fmtUGX(netRevenueNum)}</div>
                </div>
              </div>

              <div className="col-md-6 mb-2">
                <div className="border bg-white p-3" style={{ borderRadius: 16 }}>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Total Assets
                  </div>
                  <div className="font-weight-semibold">{fmtUGX(assetTotal)}</div>
                </div>
              </div>

              <div className="col-md-6 mb-2">
                <div className="border bg-white p-3" style={{ borderRadius: 16 }}>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Total Liabilities
                  </div>
                  <div className="font-weight-semibold">{fmtUGX(liabilityTotal)}</div>
                </div>
              </div>
            </div>

            <div
              className="border mt-2 p-3"
              style={{
                borderRadius: 16,
                background: balanced ? "rgba(40,167,69,.08)" : "rgba(255,193,7,.12)",
                borderColor: balanced ? "rgba(40,167,69,.25)" : "rgba(255,193,7,.35)",
              }}
            >
              <div className="d-flex align-items-center justify-content-between flex-wrap">
                <div className="d-flex align-items-center">
                  <i className={`fa ${balanced ? "fa-circle-check text-success" : "fa-triangle-exclamation text-warning"} mr-2`} />
                  <div className="font-weight-semibold">
                    {balanced ? "Balanced" : "Not balanced"}
                  </div>
                  <div className="text-muted ml-2" style={{ fontSize: 12 }}>
                    Assets must equal Liabilities + Net Result
                  </div>
                </div>

                <Button
                  label={loading ? "Please wait..." : "Confirm & Submit"}
                  className="btn btn-success"
                  onClick={() =>
                    handleMigrate({
                      ...form,
                      balanced,
                    })
                  }
                />
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Migration Assistant"
          description="Use this tool to quickly get started with your accounting setup."
          iconClass="fa fa-cogs"
        />

        <div
          className="card shadow-sm mb-5"
          style={{
            borderRadius: 18,
            border: "1px solid rgba(0,0,0,.05)",
            overflow: "hidden",
          }}
        >
          <div className="card-body">
            {renderStepNavigation()}

            <div className="row">
              {/* Left rail */}
              <div className="col-md-4">
                <div
                  className="border bg-white p-3 mb-3"
                  style={{
                    borderRadius: 18,
                    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="font-weight-semibold">Guidance</div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Step-by-step notes for accurate setup
                      </div>
                    </div>
                    <span className="badge badge-light border" style={{ fontWeight: 700 }}>
                      {step}/{steps.length}
                    </span>
                  </div>

                  <hr className="my-3" />

                  <StepNotes step={step} />
                </div>

                {/* Quick formula helper */}
                <div
                  className="border p-3"
                  style={{
                    borderRadius: 18,
                    background: "rgba(0,123,255,.04)",
                    borderColor: "rgba(0,123,255,.15)",
                  }}
                >
                  <div className="font-weight-semibold" style={{ fontSize: 13 }}>
                    Balancing rule
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Assets = Liabilities + Net Result
                  </div>
                </div>
              </div>

              {/* Main content */}
              <div className="col-md-8">
                <div
                  className="border bg-white p-3"
                  style={{
                    borderRadius: 18,
                    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
                  }}
                >
                  <div className="d-flex align-items-start justify-content-between flex-wrap mb-3">
                    <div>
                      <div className="font-weight-semibold" style={{ fontSize: 15 }}>
                        {steps[step - 1]?.title}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Provide the information below to proceed.
                      </div>
                    </div>

                    <span
                      className="badge badge-light border"
                      style={{ fontWeight: 700, borderRadius: 999, padding: "8px 12px" }}
                    >
                      <i className={`${steps[step - 1]?.icon} mr-2`} />
                      Step {step}
                    </span>
                  </div>

                  {renderStepContent()}

                  <div className="mt-4 d-flex justify-content-between">
                    <Button
                      label="Previous"
                      disabled={step === 1}
                      className="btn btn-outline-secondary"
                      onClick={() => setStep((s) => s - 1)}
                    />

                    {step < steps.length && (
                      <Button
                        label="Next"
                        className="btn btn-primary"
                        onClick={() => isStepValid() && setStep((s) => s + 1)}
                      />
                    )}
                  </div>

                  {/* Inline validation hint */}
                  {!isStepValid() && (
                    <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                      <i className="fa fa-circle-info mr-2" />
                      Please complete the required fields to proceed.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default MigrationAssistantPage;
